import { useEffect, useState } from "react";
import { db } from "../../firebase"; // ✅ Fixed path
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";

export default function OperatorManageRequests() {
  const [requests, setRequests] = useState([]);
  const [fadingOutIds, setFadingOutIds] = useState([]);

  // 🔁 Listen to shipment requests from all clients
  useEffect(() => {
    const unsubscribeFns = [];

    const listenToClientRequests = async () => {
      const clientsSnap = await getDocs(collection(db, "Clients"));

      clientsSnap.forEach((clientDoc) => {
        const clientId = clientDoc.id;
        const reqCol = collection(db, "Clients", clientId, "shipmentRequests");

        const unsub = onSnapshot(reqCol, (snap) => {
          setRequests((prev) => {
            const withoutThisClient = prev.filter((r) => r.clientId !== clientId);
            const newOnes = snap.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
              clientId,
            }));
            return [...withoutThisClient, ...newOnes];
          });
        });

        unsubscribeFns.push(unsub);
      });
    };

    listenToClientRequests();

    return () => {
      unsubscribeFns.forEach((unsub) => unsub());
    };
  }, []);

  // ✅ Approve request: convert to shipment
  const handleApprove = async (request) => {
    try {
      const shipment = {
        id: `SHIP-${Date.now()}`,
        clientId: request.clientId,
        origin: request.origin,
        destination: request.destination,
        weight: request.weight,
        cbm: request.cbm,
        pallets: request.pallets,
        containerType: request.containerType,
        mode: request.mode,
        estimatedCost: request.estimatedCost,
        status: "pending",
        notes: "",
        unitsQty: "1",
        lat: 0,
        lng: 0,
        destinationCoords: [0, 0],
        eta: "",
        createdAt: serverTimestamp(),
        timeline: [
          { label: "Pending", timestamp: null },
          { label: "Confirmed", timestamp: null },
          { label: "Booked", timestamp: null },
          { label: "Arrived", timestamp: null },
        ],
      };

      await addDoc(collection(db, "Clients", request.clientId, "shipments"), shipment);

      setFadingOutIds((prev) => [...prev, request.id]);

      setTimeout(async () => {
        await deleteDoc(doc(db, "Clients", request.clientId, "shipmentRequests", request.id));
        setRequests((prev) => prev.filter((r) => r.id !== request.id));
      }, 300);
    } catch (err) {
      console.error("❌ Failed to approve request", err);
      alert("Failed to approve request.");
    }
  };

  // ❌ Reject request
  const handleReject = async (request) => {
    try {
      setFadingOutIds((prev) => [...prev, request.id]);

      setTimeout(async () => {
        await deleteDoc(doc(db, "Clients", request.clientId, "shipmentRequests", request.id));
        setRequests((prev) => prev.filter((req) => req.id !== request.id));
      }, 300);
    } catch (err) {
      console.error("❌ Failed to reject request", err);
      alert("Failed to reject request.");
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <h2 className="text-2xl font-bold text-brand-teal flex items-center gap-2">
        📋 Manage Shipment Requests
      </h2>

      {requests.length === 0 ? (
        <div className="text-gray-500">No pending shipments.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {requests.map((req) => (
            <div
              key={req.id}
              className={`bg-white dark:bg-gray-800 p-5 rounded-xl shadow-md border transition-all duration-300 ${
                fadingOutIds.includes(req.id) ? "opacity-0 scale-95" : "opacity-100 scale-100"
              }`}
            >
              <h3 className="text-lg font-semibold mb-2">
                {req.origin} ➔ {req.destination}
              </h3>
              <p className="text-sm mb-1">
                <strong>Client:</strong> {req.clientId}
              </p>
              <p className="text-sm mb-1">
                <strong>Weight:</strong> {req.weight} kg
              </p>
              <p className="text-sm mb-1">
                <strong>CBM:</strong> {req.cbm}
              </p>
              <p className="text-sm mb-1">
                <strong>Pallets:</strong> {req.pallets || "0"}
              </p>
              <p className="text-sm mb-1">
                <strong>Container Type:</strong> {req.containerType}
              </p>
              <p className="text-sm mb-1">
                <strong>Mode:</strong> {req.mode}
              </p>
              <p className="text-sm font-bold">
                Estimated Cost: ${req.estimatedCost}
              </p>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleApprove(req)}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
                >
                  ✅ Approve
                </button>
                <button
                  onClick={() => handleReject(req)}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                >
                  ❌ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
